/**
 * `/cash/bank-accounts/new` — S32 create flow.
 *
 * **Strict-fidelity rebuild** per playbook §6. Reuses `bank-edit-page.css`
 * (lifted from `prototype/cash/bank-edit.html`) so the chrome matches edit.
 * The only structural differences vs /edit:
 *   - the composer-head h1 shows "Add bank account"
 *   - Section 1 (Identity) includes the type segmented control
 *   - Section 3 (Balance & opening) is editable (not read-only)
 *   - No live audit / danger-zone aside (only the live preview)
 *
 * @see prototype/cash/bank-edit.html
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Check,
  Clock3,
  CreditCard,
  Eye,
  FileUp,
  Landmark,
  LoaderCircle,
  Lock,
  Pencil,
  RefreshCcw,
  Search,
  Shield,
  Sparkles,
  Wallet,
  X,
} from "lucide-react";
import { Alert, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import "./bank-edit-page.css";

export const Route = createFileRoute("/(workspace)/cash/bank-accounts/new")({
  component: NewBankAccountPage,
});

type AccountType = "bank" | "cash" | "credit_card";

function NewBankAccountPage() {
  const navigate = useNavigate();
  const [type, setType] = useState<AccountType>("bank");
  const [name, setName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [openingBalanceRupees, setOpeningBalanceRupees] = useState("0");
  const [openingAsOf, setOpeningAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [imprestRupees, setImprestRupees] = useState("10000");
  const [creditLimitRupees, setCreditLimitRupees] = useState("500000");
  const [cycleDay, setCycleDay] = useState("15");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  /** IFSC auto-fill — populated when the operator clicks "Fetch" on a
      well-formed IFSC. Drives the field-help badge + auto-fills the
      Display name + Branch / MICR / SWIFT fields if the operator
      hasn't typed those yet. */
  const [ifscLookup, setIfscLookup] = useState<{
    bank: string;
    branch: string;
    city: string;
    state: string;
  } | null>(null);
  const utils = trpc.useUtils();

  // KYC fields that aren't persisted by V1 contract yet (branch / holder /
  // swift / micr) but appear on the prototype. Auto-populated from the IFSC
  // lookup so the operator sees real data; V1.x chips signal that they'll be
  // saved once the contract is widened.
  const [branch, setBranch] = useState("");
  const [holderName, setHolderName] = useState("");
  const [holderType, setHolderType] = useState<
    "company" | "founder_personal" | "partner" | "director" | "other"
  >("company");
  const [swift, setSwift] = useState("");
  const [micr, setMicr] = useState("");

  /** Credentials slide-over (account number + IFSC). Mirrors the prototype's
      S32 sensitive-details flow — credentials are one bundle behind a
      "Change →" / "Set →" affordance, so the operator can't half-set them. */
  const [credsModalOpen, setCredsModalOpen] = useState(false);
  const [draftAccountNumber, setDraftAccountNumber] = useState("");
  const [draftIfsc, setDraftIfsc] = useState("");
  const [draftIfscLookup, setDraftIfscLookup] = useState<{
    bank: string;
    branch: string;
    city: string;
    state: string;
    micr: string | null;
    swift: string | null;
  } | null>(null);
  const [draftIfscError, setDraftIfscError] = useState<string | null>(null);
  const [draftIfscLoading, setDraftIfscLoading] = useState(false);

  const draftIfscIsWellFormed = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(draftIfsc);
  const draftReady =
    draftAccountNumber.trim().length >= 6 &&
    draftAccountNumber.trim().length <= 20 &&
    draftIfscIsWellFormed;

  const openCredsModal = () => {
    setDraftAccountNumber(accountNumber);
    setDraftIfsc(ifsc);
    setDraftIfscLookup(
      ifscLookup ? { ...ifscLookup, micr: micr || null, swift: swift || null } : null,
    );
    setDraftIfscError(null);
    setCredsModalOpen(true);
  };

  const cancelCredsModal = () => {
    setCredsModalOpen(false);
    setDraftIfscError(null);
    setDraftIfscLoading(false);
  };

  const saveCredsModal = () => {
    if (!draftReady) return;
    setAccountNumber(draftAccountNumber.trim());
    setIfsc(draftIfsc.trim().toUpperCase());
    if (draftIfscLookup) {
      setIfscLookup({
        bank: draftIfscLookup.bank,
        branch: draftIfscLookup.branch,
        city: draftIfscLookup.city,
        state: draftIfscLookup.state,
      });
      if (!name.trim()) setName(`${draftIfscLookup.bank} · ${draftIfscLookup.branch}`);
      if (!branch.trim()) setBranch(draftIfscLookup.branch);
      if (!micr.trim() && draftIfscLookup.micr) setMicr(draftIfscLookup.micr);
      if (!swift.trim() && draftIfscLookup.swift) setSwift(draftIfscLookup.swift);
    }
    setCredsModalOpen(false);
  };

  /** Fetch inside the slide-over — operates on draft state so cancelling
      doesn't pollute the main form. */
  const fetchDraftIfsc = async () => {
    if (!draftIfscIsWellFormed) return;
    setDraftIfscLoading(true);
    setDraftIfscError(null);
    try {
      const r = await utils.ifsc.lookup.fetch({ ifsc: draftIfsc });
      setDraftIfscLookup({
        bank: r.bank,
        branch: r.branch,
        city: r.city,
        state: r.state,
        micr: r.micr,
        swift: r.swift,
      });
    } catch (err) {
      setDraftIfscLookup(null);
      setDraftIfscError(err instanceof Error ? err.message : "IFSC lookup failed.");
    } finally {
      setDraftIfscLoading(false);
    }
  };

  const create = trpc.bankAccounts.create.useMutation({
    onSuccess: (r) => {
      navigate({ to: "/cash/bank-accounts/$bankAccountId", params: { bankAccountId: r.account.id } });
    },
    onError: (err) => setErrorMsg(err.message),
  });

  const requiredFilled = useMemo(() => {
    if (!name.trim()) return false;
    if (type === "bank" && (!accountNumber.trim() || ifsc.trim().length !== 11)) return false;
    if (type === "credit_card") {
      if (!accountNumber.trim()) return false;
      const cd = Number(cycleDay);
      if (!Number.isInteger(cd) || cd < 1 || cd > 31) return false;
      if (!Number.isFinite(Number(creditLimitRupees)) || Number(creditLimitRupees) <= 0)
        return false;
    }
    if (type === "cash" && !Number.isFinite(Number(imprestRupees))) return false;
    return true;
  }, [type, name, accountNumber, ifsc, cycleDay, creditLimitRupees, imprestRupees]);

  const submit = () => {
    if (!requiredFilled || create.isPending) return;
    setErrorMsg(null);
    const openingPaise = String(Math.round(Number(openingBalanceRupees || "0") * 100));
    // KYC fields are optional on the contract — only forward when set so
    // they round-trip back as nulls when blank.
    const kyc = {
      ...(branch.trim() && { branch: branch.trim() }),
      ...(micr.trim() && { micrCode: micr.trim() }),
      ...(swift.trim() && { swiftBic: swift.trim().toUpperCase() }),
      ...(holderName.trim() && { holderName: holderName.trim() }),
      ...(holderType && { holderType }),
    };
    if (type === "bank") {
      create.mutate({
        type: "bank",
        name: name.trim(),
        accountNumberPlaintext: accountNumber.trim(),
        ifscPlaintext: ifsc.trim().toUpperCase(),
        openingBalancePaise: openingPaise,
        openingBalanceAsOf: openingAsOf,
        ...kyc,
      });
    } else if (type === "cash") {
      create.mutate({
        type: "cash",
        name: name.trim(),
        imprestAmountPaise: String(Math.round(Number(imprestRupees || "0") * 100)),
        openingBalancePaise: openingPaise,
        openingBalanceAsOf: openingAsOf,
      });
    } else {
      create.mutate({
        type: "credit_card",
        name: name.trim(),
        accountNumberPlaintext: accountNumber.trim(),
        creditLimitPaise: String(Math.round(Number(creditLimitRupees || "0") * 100)),
        statementCycleDay: Number(cycleDay),
        openingBalancePaise: openingPaise,
        openingBalanceAsOf: openingAsOf,
        ...kyc,
      });
    }
  };

  const ava = avatarFor({ name, type });
  const parsed = parseAccountName(name || "New account");

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Cash" },
        { label: "Bank accounts", to: "/cash/bank-accounts" },
        { label: "Add account" },
      ]}
      /* Same composer-head treatment as /edit — sticky head pinned to
         the top of the scroll viewport, full-width, no shell padding. */
      viewportClassName="!p-0"
      contentClassName="!max-w-full !mx-0"
    >
      <div className="bank-edit-page">
        {/* ── Composer head ─────────────────────────────── */}
        <header className="composer-head">
          <Link
            to="/cash/bank-accounts"
            className="composer-head-back"
            aria-label="Back to bank accounts"
          >
            <ArrowLeft size={16} strokeWidth={2} aria-hidden />
          </Link>
          <div className="composer-head-text">
            <h1>
              Add bank account
              <span className="role-pill">{labelForType(type)}</span>
            </h1>
            <p className="sub">
              <span>
                Pick the account type. Each type captures only the fields it actually uses.
              </span>
              <span className="dot" aria-hidden="true" />
              <span>Step-up + 24h cooling-off applies on later credentials changes</span>
            </p>
          </div>
          <span className="save-status dirty" aria-live="polite">
            Unsaved draft
          </span>
          <div className="composer-head-actions">
            <Link to="/cash/bank-accounts" className="btn-secondary">
              Cancel
            </Link>
            <button
              type="button"
              className="btn-primary"
              disabled={!requiredFilled || create.isPending}
              onClick={submit}
            >
              <Check size={13} strokeWidth={2} aria-hidden />
              {create.isPending ? "Creating…" : "Create account"}
            </button>
          </div>
        </header>

        <div className="composer-body">
          {/* ── LEFT: form sections ───────────────────────── */}
          <main className="composer-form">
            {errorMsg ? (
              <Alert intent="bad" title="Couldn't create the account" description={errorMsg} />
            ) : null}

            {/* Section 1 — Identity (mirrors prototype Account-identity layout) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <span className="step">1</span> Account identity
                  </h3>
                  <p className="sub">
                    Display name, type, currency and the chart-of-account code this maps to.
                  </p>
                </div>
                <div className="right">
                  <span className="pill ok">Required</span>
                </div>
              </div>
              <div className="section-body">
                {/* Row 1 — Display name + Internal code (V1.x) */}
                <div className="field-grid cols-2">
                  <div className="field">
                    <label>
                      Display name <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      className="field-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={
                        type === "cash"
                          ? "e.g. Petty cash · Bhosari office"
                          : type === "credit_card"
                            ? "e.g. HDFC Regalia · ****4421"
                            : "e.g. HDFC Bank · Current ···4421"
                      }
                    />
                    <span className="field-help">
                      Shown in lists, invoices, payment receipts and Ask Flint answers. Use the
                      bank-then-account-label shape (e.g.{" "}
                      <code
                        style={{
                          fontFamily: "Geist Mono, ui-monospace, monospace",
                          fontSize: 12,
                          padding: "0 4px",
                          borderRadius: 3,
                          background: "var(--bg-local)",
                        }}
                      >
                        HDFC Bank · Current ···4421
                      </code>
                      ) so the list-row splits cleanly.
                    </span>
                  </div>
                  <div className="field is-mono">
                    <label>
                      Internal code{" "}
                      <span
                        style={{
                          marginLeft: 4,
                          padding: "1px 6px",
                          borderRadius: 3,
                          background: "var(--bg-local)",
                          color: "var(--muted-local)",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.06,
                          textTransform: "uppercase",
                          border: "1px solid var(--line-local)",
                        }}
                      >
                        V1.x
                      </span>
                    </label>
                    <input
                      type="text"
                      className="field-input"
                      placeholder="HDFC-CURR-4421"
                      disabled
                    />
                    <span className="field-help">
                      Used to map to the Tally / chart-of-accounts ledger on export.
                    </span>
                  </div>
                </div>

                {/* Row 2 — Account type segmented control (6 buttons; 3 V1.x disabled) */}
                <div style={{ marginTop: 14 }}>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--ink-2-local)",
                      display: "block",
                      marginBottom: 6,
                    }}
                  >
                    Account type{" "}
                    <span className="req" style={{ color: "var(--rose)" }}>
                      *
                    </span>
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 12,
                        color: "var(--muted-local)",
                        fontWeight: 500,
                        textTransform: "none",
                        letterSpacing: 0,
                      }}
                    >
                      · locks after creation
                    </span>
                  </label>
                  <div className="seg-group" role="radiogroup" aria-label="Account type">
                    <SegBtn
                      label="Current"
                      icon={<Landmark className="ic" strokeWidth={2} aria-hidden />}
                      active={type === "bank"}
                      onClick={() => setType("bank")}
                    />
                    <SegBtnDisabled
                      label="Savings"
                      icon={<Clock3 className="ic" strokeWidth={2} aria-hidden />}
                    />
                    <SegBtnDisabled
                      label="Overdraft / CC"
                      icon={<Sparkles className="ic" strokeWidth={2} aria-hidden />}
                    />
                    <SegBtn
                      label="Credit card"
                      icon={<CreditCard className="ic" strokeWidth={2} aria-hidden />}
                      active={type === "credit_card"}
                      onClick={() => setType("credit_card")}
                    />
                    <SegBtnDisabled
                      label="Wallet / UPI"
                      icon={<Wallet className="ic" strokeWidth={2} aria-hidden />}
                    />
                    <SegBtn
                      label="Cash on hand"
                      icon={<Wallet className="ic" strokeWidth={2} aria-hidden />}
                      active={type === "cash"}
                      onClick={() => setType("cash")}
                    />
                  </div>
                </div>

                {/* Row 3 — Currency + Group on chart of accounts (V1.x disabled) */}
                <div className="field-grid cols-2" style={{ marginTop: 14 }}>
                  <div className="field">
                    <label>
                      Currency <span className="req">*</span>
                    </label>
                    <Select value="INR" disabled>
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue placeholder="INR" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INR">INR · Indian Rupee (₹)</SelectItem>
                        <SelectItem value="USD">USD · US Dollar ($)</SelectItem>
                        <SelectItem value="EUR">EUR · Euro (€)</SelectItem>
                        <SelectItem value="GBP">GBP · British Pound (£)</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="field-help">
                      All postings to this account are stored in this currency.
                    </span>
                  </div>
                  <div className="field">
                    <label>
                      Group on chart of accounts{" "}
                      <span
                        style={{
                          marginLeft: 4,
                          padding: "1px 6px",
                          borderRadius: 3,
                          background: "var(--bg-local)",
                          color: "var(--muted-local)",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.06,
                          textTransform: "uppercase",
                          border: "1px solid var(--line-local)",
                        }}
                      >
                        V1.x
                      </span>
                    </label>
                    <Select disabled value={defaultCoaGroup(type)}>
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={defaultCoaGroup(type)}>
                          {defaultCoaGroup(type)}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="field-help">
                      Drives placement in trial balance &amp; balance sheet. Lands with V2 GL
                      projection.
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2 — KYC / credentials (only for bank + credit_card) */}
            {type !== "cash" ? (
              <section className="section">
                <div className="section-head">
                  <div>
                    <h3>
                      <span className="step">2</span>{" "}
                      {type === "credit_card" ? "Card details" : "Bank & KYC details"}
                    </h3>
                    <p className="sub">
                      {type === "credit_card"
                        ? "Last-4 of the PAN + sanctioned limit + statement cycle day."
                        : "Click Fetch on the IFSC field — bank, branch, city & MICR auto-fill from the RBI directory."}
                    </p>
                  </div>
                  <div className="right">
                    <span className="pill ok">Required</span>
                  </div>
                </div>

                {type === "bank" ? (
                  <div className="section-body">
                    {/* Row 1 — Bank + IFSC (read-only display) + Branch. Credentials
                      are entered via the slide-over below; this row just
                      reflects what is in state so the operator can confirm at
                      a glance. Mirrors prototype lines 1288-1320. */}
                    <div className="field-grid cols-3-2">
                      <div className="field">
                        <label>
                          Bank <span className="req">*</span>
                        </label>
                        <input
                          type="text"
                          className="field-input"
                          value={ifscLookup?.bank ?? ""}
                          placeholder="Set via the credentials block below"
                          readOnly
                          disabled
                        />
                        <span className="field-help">
                          Auto-locked from the IFSC — change only if the IFSC is wrong.
                        </span>
                      </div>
                      <div className="field is-mono">
                        <label>
                          IFSC code <span className="req">*</span>
                        </label>
                        <div className="field-affix">
                          <input
                            type="text"
                            className="field-input"
                            value={ifsc}
                            placeholder="Set via the credentials block below"
                            readOnly
                            disabled
                            aria-describedby="ifsc-help"
                          />
                          {ifscLookup ? (
                            <span className="badge">
                              <Check size={9} strokeWidth={3} aria-hidden />
                              Verified
                            </span>
                          ) : null}
                        </div>
                        <span id="ifsc-help" className={`field-help${ifscLookup ? " is-ok" : ""}`}>
                          {ifscLookup
                            ? `Matches ${ifscLookup.bank} · ${ifscLookup.branch}, ${ifscLookup.city}`
                            : "Open the credentials block below to set the IFSC."}
                        </span>
                      </div>
                      <div className="field">
                        <label>Branch</label>
                        <input
                          type="text"
                          className="field-input"
                          value={branch}
                          onChange={(e) => setBranch(e.target.value)}
                          placeholder={ifscLookup ? "" : "Auto-fills after credentials are set"}
                        />
                        <span className="field-help">
                          Auto-filled from IFSC. Edit if printed differently on cheques.
                        </span>
                      </div>
                    </div>

                    {/* The big gray sensitive-credentials block — display-only
                      summary with a Change/Set button that opens the
                      slide-over. Mirrors prototype lines 1322-1347. */}
                    <div style={{ marginTop: 14 }}>
                      <label
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--ink-2-local)",
                          display: "block",
                          marginBottom: 6,
                        }}
                      >
                        Account number &amp; IFSC{" "}
                        <span className="req" style={{ color: "var(--rose)" }}>
                          *
                        </span>
                      </label>
                      <div className="sensitive-locked">
                        <div className="lock-ic" aria-hidden="true">
                          <Lock size={16} strokeWidth={2} aria-hidden />
                        </div>
                        <div className="lock-text">
                          <strong>
                            Current:{" "}
                            <span className="mono">
                              {accountNumber.length >= 6 && ifsc.length === 11
                                ? `····${accountNumber.slice(-4)} · ${ifsc}`
                                : "not yet set"}
                            </span>
                          </strong>
                          <span>
                            {accountNumber.length >= 6 && ifsc.length === 11
                              ? "Stored encrypted at rest. Changes after creation require Founder OTP + 24-hour cooling-off before any outbound payment can use the new credentials."
                              : "Click Set to enter the account number and IFSC. Credentials are stored encrypted; only the last-4 surfaces in lists."}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="unlock"
                          onClick={openCredsModal}
                          aria-haspopup="dialog"
                        >
                          <Pencil size={12} strokeWidth={2} aria-hidden />
                          {accountNumber.length >= 6 && ifsc.length === 11 ? "Change →" : "Set →"}
                        </button>
                      </div>
                    </div>

                    {/* Row 2 — Holder name + Holder type + SWIFT (mirror lines 1349-1370) */}
                    <div className="field-grid cols-3" style={{ marginTop: 14 }}>
                      <div className="field">
                        <label>Account holder name</label>
                        <input
                          type="text"
                          className="field-input"
                          value={holderName}
                          onChange={(e) => setHolderName(e.target.value)}
                          placeholder="Match the name on cheque / passbook"
                        />
                        <span className="field-help">
                          Must match the name on the cheque / passbook for vendor payouts to clear.
                        </span>
                      </div>
                      <div className="field">
                        <label>Holder type</label>
                        <Select
                          value={holderType}
                          onValueChange={(v) => setHolderType(v as typeof holderType)}
                        >
                          <SelectTrigger className="!h-9 !text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="company">Company</SelectItem>
                            <SelectItem value="founder_personal">Founder personal</SelectItem>
                            <SelectItem value="partner">Partner</SelectItem>
                            <SelectItem value="director">Director</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="field-help">
                          Drives the GST / TDS challan signatory and the auto-narration on the
                          printed invoice.
                        </span>
                      </div>
                      <div className="field is-mono">
                        <label>SWIFT / BIC</label>
                        <input
                          type="text"
                          className="field-input"
                          value={swift}
                          onChange={(e) => setSwift(e.target.value.toUpperCase())}
                          placeholder={
                            ifscLookup?.bank
                              ? `Optional · e.g. ${ifscLookup.bank.slice(0, 4).toUpperCase()}INBB`
                              : "Optional"
                          }
                          maxLength={11}
                        />
                        <span className="field-help">
                          Required only for foreign-currency wires.
                        </span>
                      </div>
                    </div>

                    {/* Row 3 — BSR + MICR + Linked GSTIN slot (mirror lines 1372-1392) */}
                    <div className="field-grid cols-3" style={{ marginTop: 14 }}>
                      <div className="field is-mono">
                        <label>
                          BSR code <V1xChip />
                        </label>
                        <input
                          type="text"
                          className="field-input"
                          placeholder="7 digits"
                          disabled
                        />
                        <span className="field-help">
                          Used for TDS challan generation. RBI-allotted, 7 digits.
                        </span>
                      </div>
                      <div className="field is-mono">
                        <label>MICR code</label>
                        <input
                          type="text"
                          className="field-input"
                          value={micr}
                          onChange={(e) => setMicr(e.target.value)}
                          placeholder={ifscLookup ? "—" : "9 digits · auto-fills after Fetch"}
                          maxLength={9}
                        />
                        <span className="field-help">
                          Printed at the bottom of cheques. Optional.
                        </span>
                      </div>
                      <div className="field">
                        <label>
                          Linked GSTIN bank slot <V1xChip />
                        </label>
                        <Select disabled value="slot1">
                          <SelectTrigger className="!h-9 !text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="slot1">Slot 1 — primary on GST portal</SelectItem>
                            <SelectItem value="slot2">Slot 2 — secondary</SelectItem>
                            <SelectItem value="not_linked">Not linked to GST portal</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="field-help">
                          Required under Rule 10A within 45 days of registration.
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}

                {type === "credit_card" ? (
                  <div className="section-body">
                    <div className="field-grid cols-2">
                      <div className="field is-mono">
                        <label>
                          Card last-4 (PAN) <span className="req">*</span>
                        </label>
                        <input
                          type="text"
                          className="field-input"
                          value={accountNumber}
                          onChange={(e) => setAccountNumber(e.target.value)}
                          placeholder="4421"
                          maxLength={20}
                        />
                        <span className="field-help">
                          Stored as <span className="mono">v1!&lt;base64&gt;</span> wrap; only the
                          last-4 surfaces in lists.
                        </span>
                      </div>
                      <div className="field is-mono">
                        <label>
                          Credit limit (₹) <span className="req">*</span>
                        </label>
                        <input
                          type="text"
                          className="field-input"
                          value={creditLimitRupees}
                          onChange={(e) => setCreditLimitRupees(e.target.value)}
                          placeholder="500000"
                        />
                        <span className="field-help">Sanctioned by the issuer.</span>
                      </div>
                      <div className="field">
                        <label>
                          Statement cycle day (1–31) <span className="req">*</span>
                        </label>
                        <input
                          type="text"
                          className="field-input"
                          value={cycleDay}
                          onChange={(e) => setCycleDay(e.target.value)}
                          placeholder="15"
                          maxLength={2}
                        />
                        <span className="field-help">
                          Day-of-month the statement cuts. Day 29-31 normalises to month-end.
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}

                {type === "bank" ? (
                  <div className="section-foot">
                    <Shield size={13} strokeWidth={2} aria-hidden />
                    Saved encrypted at rest. After creation, any change to the account number or
                    IFSC requires a step-up OTP + 24-hour cooling-off before outbound transfers
                    resume (ADR-0008).
                  </div>
                ) : null}
              </section>
            ) : null}

            {/* Section 3 — Opening balance & reconciliation start
                (mirror prototype/cash/bank-edit.html lines 1397-1449) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <span className="step">3</span> Opening balance &amp; reconciliation start
                  </h3>
                  <p className="sub">
                    The balance Flint should treat as the starting point. Used as the first row when
                    matching statement to ledger.
                  </p>
                </div>
                <div className="right">
                  <span className="pill ok">Required</span>
                </div>
              </div>
              <div className="section-body">
                <div className="field-grid cols-3-2">
                  <div className="field is-mono">
                    <label>
                      Opening balance <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      className="field-input"
                      value={openingBalanceRupees}
                      onChange={(e) => setOpeningBalanceRupees(e.target.value)}
                      placeholder="0"
                    />
                    <span className="field-help">
                      Positive for funds in account, negative for OD drawn. Locks after first money
                      event.
                    </span>
                  </div>
                  <div className="field is-mono">
                    <label>
                      As of date <span className="req">*</span>
                    </label>
                    <input
                      type="date"
                      className="field-input"
                      value={openingAsOf}
                      onChange={(e) => setOpeningAsOf(e.target.value)}
                    />
                    <span className="field-help">
                      Books beginning · the date the balance above was correct.
                    </span>
                  </div>
                  <div className="field">
                    <label>
                      Reconcile against <V1xChip />
                    </label>
                    <Select disabled value="opening_journal">
                      <SelectTrigger className="!h-9 !text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="opening_journal">Opening journal entry</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="field-help">Drives the very first row of the BRS.</span>
                  </div>
                </div>

                {type === "cash" ? (
                  <div className="field-grid cols-2" style={{ marginTop: 14 }}>
                    <div className="field is-mono">
                      <label>
                        Imprest ceiling <span className="req">*</span>
                      </label>
                      <input
                        type="text"
                        className="field-input"
                        aria-label="Imprest ceiling"
                        value={imprestRupees}
                        onChange={(e) => setImprestRupees(e.target.value)}
                        placeholder="Ceiling amount"
                      />
                      <span className="field-help">
                        Drawer balance must stay at or below this ceiling (Imprest System, ADR-0012
                        §10).
                      </span>
                    </div>
                  </div>
                ) : null}

                {type === "credit_card" ? (
                  <div className="field-grid cols-2" style={{ marginTop: 14 }}>
                    <div className="field is-mono">
                      <label>
                        Credit limit <span className="req">*</span>
                      </label>
                      <input
                        type="text"
                        className="field-input"
                        value={creditLimitRupees}
                        onChange={(e) => setCreditLimitRupees(e.target.value)}
                        placeholder="500000"
                      />
                    </div>
                    <div className="field is-mono">
                      <label>
                        Statement cycle day (1-31) <span className="req">*</span>
                      </label>
                      <input
                        type="text"
                        className="field-input"
                        value={cycleDay}
                        onChange={(e) => setCycleDay(e.target.value)}
                        placeholder="15"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </section>

            {/* Section 4 — Connection & sync
                (mirror prototype/cash/bank-edit.html lines 1451-1549) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <span className="step">4</span> Connection &amp; sync
                  </h3>
                  <p className="sub">
                    How transactions land in Flint. Pick auto-feed for daily sync, statement upload
                    for monthly close, or manual for petty / dormant accounts.
                  </p>
                </div>
                <div className="right">
                  <span className="pill v1x">V1.x</span>
                </div>
              </div>
              <div className="section-body">
                <div className="conn-grid" role="radiogroup" aria-label="Connection method">
                  <button
                    type="button"
                    className="conn-card"
                    role="radio"
                    aria-checked="false"
                    disabled
                  >
                    <span className="v1x-chip">V1.x</span>
                    <span className="check" aria-hidden="true" />
                    <span className="ic" aria-hidden="true">
                      <RefreshCcw size={14} strokeWidth={2} aria-hidden />
                    </span>
                    <strong>Auto-sync (bank feed)</strong>
                    <span>Yodlee · refreshes every 4 hours · 18 months of history pulled in</span>
                  </button>
                  <button
                    type="button"
                    className="conn-card"
                    role="radio"
                    aria-checked="false"
                    disabled
                  >
                    <span className="v1x-chip">V1.x</span>
                    <span className="check" aria-hidden="true" />
                    <span className="ic" aria-hidden="true">
                      <FileUp size={14} strokeWidth={2} aria-hidden />
                    </span>
                    <strong>Statement upload</strong>
                    <span>Drop CSV / PDF / Excel statements. Flint parses and de-duplicates.</span>
                  </button>
                  <button
                    type="button"
                    className="conn-card is-on"
                    role="radio"
                    aria-checked="true"
                  >
                    <span className="check" aria-hidden="true" />
                    <span className="ic" aria-hidden="true">
                      <Pencil size={12} strokeWidth={2} aria-hidden />
                    </span>
                    <strong>Manual entry</strong>
                    <span>
                      Every receipt / vendor payment / cash movement is booked manually against this
                      account.
                    </span>
                  </button>
                </div>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--muted-local)",
                    marginTop: 14,
                    lineHeight: 1.5,
                  }}
                >
                  Sync method can be changed after the account is created. Auto-feed + CSV upload
                  land in V1.x — until then every txn is sourced from the operator's manual entry.
                </p>
              </div>
            </section>
          </main>

          {/* ── RIGHT: aside ─────────────────────────────── */}
          <aside className="composer-aside" aria-label="Live preview &amp; meta">
            {/* Live preview (mirror prototype lines 1702-1749) */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <Eye size={13} strokeWidth={2} aria-hidden />
                    Live preview
                  </h3>
                  <p className="sub">How this account will appear across Flint.</p>
                </div>
              </div>
              <div
                className="section-body"
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                <div>
                  <div className="preview-section-h">In bank accounts list</div>
                  <div className="preview-list-row">
                    <div className={`preview-ava ${ava.classMod}`} aria-hidden="true">
                      {ava.label}
                    </div>
                    <div className="preview-row-body">
                      <strong>
                        {parsed.bank || "New account"}
                        {parsed.acc ? <span className="acc">{parsed.acc}</span> : null}
                      </strong>
                      <small>{labelForType(type)} · pending first money event</small>
                    </div>
                    <div className="preview-row-bal">
                      ₹{Number(openingBalanceRupees || 0).toLocaleString("en-IN")}
                      <small>opening</small>
                    </div>
                  </div>
                </div>

                {type === "bank" ? (
                  <div>
                    <div className="preview-section-h">On invoice footer</div>
                    <div className="pay-block">
                      <div className="h">Pay to · NEFT / RTGS / IMPS</div>
                      <div className="row">
                        <span>Bank</span>
                        <strong className="is-sans">{parsed.bank || "—"}</strong>
                      </div>
                      <div className="row">
                        <span>A/c name</span>
                        <strong className="is-sans">{parsed.bank || "—"}</strong>
                      </div>
                      <div className="row">
                        <span>A/c no</span>
                        <strong>
                          {accountNumber ? `········${accountNumber.slice(-4)}` : "—"}
                        </strong>
                      </div>
                      <div className="row">
                        <span>IFSC</span>
                        <strong>{ifsc || "—"}</strong>
                      </div>
                      <div className="qr-row">
                        <div className="qr" aria-hidden="true" />
                        <div className="qr-meta">
                          <strong>Scan &amp; pay (UPI)</strong>
                          <small>UPI QR generation lands in V1.x</small>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </section>

            {/* What happens next */}
            <section className="section">
              <div className="section-head">
                <div>
                  <h3>
                    <Shield size={13} strokeWidth={2} aria-hidden />
                    What happens next
                  </h3>
                  <p className="sub">On create.</p>
                </div>
              </div>
              <div className="section-body">
                <p
                  style={{ fontSize: 12, color: "var(--muted-local)", margin: 0, lineHeight: 1.55 }}
                >
                  Flint writes a{" "}
                  <code style={{ fontFamily: "'Geist Mono', monospace" }}>bank_accounts</code> row +
                  an audit entry, and lands you on the detail page. Receipts, vendor payments and
                  cash movements posted against this account will appear in the activity stream
                  immediately.
                </p>
              </div>
            </section>
          </aside>
        </div>
      </div>

      {/* Credentials slide-over — mirrors prototype/cash/bank-edit.html lines 927-1029
          (S32 sensitive-details flow). On /new it captures the account number
          + IFSC together so they can't be half-set; on /edit (V1.0.x) it'll
          wrap a step-up OTP before commit. */}
      {credsModalOpen ? (
        <CredsSlideOver
          draftAccountNumber={draftAccountNumber}
          setDraftAccountNumber={setDraftAccountNumber}
          draftIfsc={draftIfsc}
          onDraftIfscChange={(v) => {
            setDraftIfsc(v);
            if (draftIfscLookup || draftIfscError) {
              setDraftIfscLookup(null);
              setDraftIfscError(null);
            }
          }}
          draftIfscLookup={draftIfscLookup}
          draftIfscError={draftIfscError}
          draftIfscLoading={draftIfscLoading}
          draftIfscIsWellFormed={draftIfscIsWellFormed}
          draftReady={draftReady}
          onFetch={fetchDraftIfsc}
          onSave={saveCredsModal}
          onCancel={cancelCredsModal}
        />
      ) : null}
    </WorkspaceShell>
  );
}

// ── Credentials slide-over ───────────────────────────────

interface CredsSlideOverProps {
  draftAccountNumber: string;
  setDraftAccountNumber: (v: string) => void;
  draftIfsc: string;
  onDraftIfscChange: (v: string) => void;
  draftIfscLookup: {
    bank: string;
    branch: string;
    city: string;
    state: string;
    micr: string | null;
    swift: string | null;
  } | null;
  draftIfscError: string | null;
  draftIfscLoading: boolean;
  draftIfscIsWellFormed: boolean;
  draftReady: boolean;
  onFetch: () => void;
  onSave: () => void;
  onCancel: () => void;
}

function CredsSlideOver(props: CredsSlideOverProps) {
  return (
    <div
      className="s32-scrim show"
      onClick={(e) => {
        // Click on the scrim itself (not on the dialog) closes the modal.
        if (e.target === e.currentTarget) props.onCancel();
      }}
    >
      <div
        className="s32 show"
        role="dialog"
        aria-modal="true"
        aria-label="Set bank credentials"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="s32-head">
          <div className="s32-head-text">
            <h2>Bank credentials</h2>
            <p>
              Enter the account number and IFSC. Stored encrypted at rest; only the last-4 ever
              surfaces in lists.
            </p>
          </div>
          <button type="button" className="s32-close" onClick={props.onCancel} aria-label="Close">
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="s32-body">
          <div className="field is-mono" style={{ marginBottom: 14 }}>
            <label>
              Account number <span className="req">*</span>
            </label>
            <input
              type="text"
              className="field-input"
              value={props.draftAccountNumber}
              onChange={(e) => props.setDraftAccountNumber(e.target.value)}
              placeholder="50100123456789"
              maxLength={64}
              autoFocus
            />
            <span className="field-help">6–20 digits. Envelope-wrapped at rest.</span>
          </div>
          <div className="field is-mono" style={{ marginBottom: 14 }}>
            <label>
              IFSC code <span className="req">*</span>
            </label>
            <div className="field-affix">
              <input
                type="text"
                className="field-input"
                value={props.draftIfsc}
                onChange={(e) => props.onDraftIfscChange(e.target.value.toUpperCase())}
                placeholder="HDFC0001247"
                maxLength={11}
              />
              <button
                type="button"
                className="field-affix-action"
                onClick={props.onFetch}
                disabled={!props.draftIfscIsWellFormed || props.draftIfscLoading}
                title={
                  props.draftIfscIsWellFormed
                    ? "Fetch bank + branch from RBI directory"
                    : "Enter a valid 11-character IFSC first"
                }
              >
                {props.draftIfscLoading ? (
                  <LoaderCircle
                    size={12}
                    strokeWidth={2}
                    aria-hidden
                    style={{ animation: "flint-spin 0.8s linear infinite" }}
                  />
                ) : (
                  <Search size={12} strokeWidth={2} aria-hidden />
                )}
                {props.draftIfscLoading ? "Fetching…" : "Fetch"}
              </button>
            </div>
            <span
              className={`field-help${props.draftIfscLookup ? " is-ok" : props.draftIfscError ? " is-error" : ""}`}
            >
              {props.draftIfscLookup
                ? `✓ ${props.draftIfscLookup.bank} · ${props.draftIfscLookup.branch}, ${props.draftIfscLookup.city}`
                : props.draftIfscError
                  ? props.draftIfscError
                  : `11-char RBI code · ${props.draftIfsc.length}/11${props.draftIfscIsWellFormed ? " · click Fetch to verify" : ""}.`}
            </span>
          </div>

          <div
            style={{
              padding: 12,
              background: "#fefce8",
              border: "1px solid #fef9c3",
              borderRadius: 6,
              color: "#854d0e",
              fontSize: 12,
              lineHeight: 1.55,
            }}
          >
            <strong style={{ fontWeight: 700 }}>Step-up middleware ships in V1.0.x.</strong> In
            production the slide-over will require Founder OTP before commit, then enforce a 24-hour
            cooling-off window during which no outbound payment can use the new credentials
            (ADR-0008).
          </div>
        </div>
        <div
          style={{
            padding: 14,
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button type="button" className="btn-secondary" onClick={props.onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={props.onSave}
            disabled={!props.draftReady}
          >
            <Check size={13} strokeWidth={2} aria-hidden />
            Save credentials
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Segmented-control type tiles ─────────────────────────

function SegBtn({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      className={active ? "is-active" : ""}
      onClick={onClick}
    >
      {icon}
      {label}
    </button>
  );
}

function SegBtnDisabled({ label, icon }: { label: string; icon: React.ReactNode }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={false}
      disabled
      title={`${label} sub-type lands in V1.x`}
    >
      {icon}
      {label}
      <span
        style={{
          marginLeft: 4,
          padding: "0 4px",
          borderRadius: 3,
          background: "var(--bg-local)",
          color: "var(--muted-local)",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.06,
          textTransform: "uppercase",
          border: "1px solid var(--line-local)",
        }}
      >
        V1.x
      </span>
    </button>
  );
}

function defaultCoaGroup(type: AccountType): string {
  switch (type) {
    case "cash":
      return "Cash in hand";
    case "credit_card":
      return "Credit card liability";
    default:
      return "Bank accounts (current assets)";
  }
}

// ── Helpers (shared with /edit) ──────────────────────────

function labelForType(t: AccountType): string {
  switch (t) {
    case "cash":
      return "Petty cash";
    case "credit_card":
      return "Credit card";
    default:
      return "Operating bank";
  }
}

function parseAccountName(name: string): { bank: string; acc: string | null } {
  const sep = " · ";
  const idx = name.indexOf(sep);
  if (idx === -1) return { bank: name, acc: null };
  return { bank: name.slice(0, idx), acc: name.slice(idx + sep.length) };
}

function avatarFor(args: { name: string; type: AccountType }): { classMod: string; label: string } {
  if (args.type === "cash") return { classMod: "b-cash", label: "₹" };
  if (args.type === "credit_card") return { classMod: "b-cc", label: "VISA" };
  const n = args.name.toLowerCase();
  if (n.includes("hdfc")) return { classMod: "b-hdfc", label: "H" };
  if (n.includes("icici")) return { classMod: "b-icici", label: "I" };
  if (n.includes("kotak")) return { classMod: "b-kotak", label: "K" };
  if (n.includes("axis")) return { classMod: "b-axis", label: "A" };
  if (n.includes("rbl")) return { classMod: "b-rbl", label: "R" };
  return { classMod: "b-hdfc", label: args.name.charAt(0).toUpperCase() || "?" };
}

// ── Icons ───────────────────────────────────────────────

/** Small inline `V1.x` chip used beside field labels for deferred fields. */
function V1xChip() {
  return (
    <span
      style={{
        marginLeft: 4,
        padding: "1px 6px",
        borderRadius: 3,
        background: "var(--bg-local)",
        color: "var(--muted-local)",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.06,
        textTransform: "uppercase",
        border: "1px solid var(--line-local)",
      }}
    >
      V1.x
    </span>
  );
}

/* Sections 3-5 + aside icons. Mirrored from $bankAccountId.edit.tsx so the
   two surfaces share their icon definitions. */

/* Type-tile icons. */
